import { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';

function getHoverTarget(target) {
  if (!(target instanceof Element)) return null;
  return target.closest(
    '[data-cursor-context], [data-magnetic], a, button, input, textarea, [data-liquid-interactive]'
  );
}

export default function Cursor({ systemSnapshot }) {
  const [isFinePointer, setIsFinePointer] = useState(false);
  // Only state that triggers a visual size change (fires infrequently — on hover only)
  const [cursorSizes, setCursorSizes] = useState({ loom: 46, glow: 62, glowOpacity: 0.52 });

  const hoveredElementRef = useRef(null);
  const hoverRectRef = useRef(null);        // live rect — updated without setState
  const systemSnapshotRef = useRef(systemSnapshot);
  useEffect(() => { systemSnapshotRef.current = systemSnapshot; }, [systemSnapshot]);

  // ── Motion values (no React renders on mouse move) ──────────────────────
  const rawX = useMotionValue(-200);
  const rawY = useMotionValue(-200);
  const rotateMV = useMotionValue(0);
  const dotRawX = useMotionValue(-200);
  const dotRawY = useMotionValue(-200);

  // Loom cursor spring
  const loomX = useSpring(rawX, { stiffness: 360, damping: 30, mass: 0.55 });
  const loomY = useSpring(rawY, { stiffness: 360, damping: 30, mass: 0.55 });
  // Glow spring (slower)
  const glowX = useSpring(rawX, { stiffness: 220, damping: 26, mass: 0.85 });
  const glowY = useSpring(rawY, { stiffness: 220, damping: 26, mass: 0.85 });
  // Dot spring (snappiest)
  const dotX = useSpring(dotRawX, { stiffness: 1200, damping: 60, mass: 0.12 });
  const dotY = useSpring(dotRawY, { stiffness: 1200, damping: 60, mass: 0.12 });
  // Rotate spring
  const rotateSpring = useSpring(rotateMV, { stiffness: 360, damping: 30, mass: 0.55 });

  // ── Fine-pointer detection ───────────────────────────────────────────────
  useEffect(() => {
    const mq = window.matchMedia('(pointer: fine)');
    const update = () => setIsFinePointer(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  useEffect(() => {
    if (!isFinePointer) return undefined;
    const prev = document.body.style.cursor;
    document.body.style.cursor = 'none';
    return () => { document.body.style.cursor = prev; };
  }, [isFinePointer]);

  // ── Mouse tracking (zero setState on mouse move) ─────────────────────────
  useEffect(() => {
    if (!isFinePointer) return undefined;

    const refreshHoverRect = () => {
      hoverRectRef.current = hoveredElementRef.current?.getBoundingClientRect() ?? null;
    };

    const assignTarget = (target) => {
      hoveredElementRef.current = target;
      refreshHoverRect();
      const hasMeta = target instanceof Element;
      const hasRect = !!hoverRectRef.current;
      setCursorSizes({
        loom: hasMeta ? 74 : hasRect ? 58 : 46,
        glow: hasMeta ? 128 : hasRect ? 88 : 62,
        glowOpacity: hasMeta ? 0.92 : 0.52,
      });
    };

    const onMouseMove = (e) => {
      const mx = e.clientX;
      const my = e.clientY;

      dotRawX.set(mx - 4);
      dotRawY.set(my - 4);

      const rect = hoverRectRef.current;
      if (rect) {
        const strength = 0.62 + (systemSnapshotRef.current?.loadIntensity ?? 0.25) * 0.18;
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        rawX.set(mx + (cx - mx) * strength);
        rawY.set(my + (cy - my) * strength);
        rotateMV.set((mx - cx) * 0.04);
      } else {
        rawX.set(mx);
        rawY.set(my);
        rotateMV.set(0);
      }
    };

    const onMouseOver = (e) => assignTarget(getHoverTarget(e.target));
    const onMouseOut = (e) => {
      const rel = getHoverTarget(e.relatedTarget);
      if (rel) { assignTarget(rel); return; }
      hoveredElementRef.current = null;
      hoverRectRef.current = null;
      setCursorSizes({ loom: 46, glow: 62, glowOpacity: 0.52 });
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseover', onMouseOver);
    window.addEventListener('mouseout', onMouseOut);
    window.addEventListener('resize', refreshHoverRect);
    // scroll: only refresh the cached rect, no setState
    window.addEventListener('scroll', refreshHoverRect, { passive: true, capture: true });

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseover', onMouseOver);
      window.removeEventListener('mouseout', onMouseOut);
      window.removeEventListener('resize', refreshHoverRect);
      window.removeEventListener('scroll', refreshHoverRect, true);
    };
  }, [isFinePointer, rawX, rawY, rotateMV, dotRawX, dotRawY]);

  if (!isFinePointer) return null;

  const { loom: loomSize, glow: glowSize, glowOpacity } = cursorSizes;
  const sizeTransition = 'width 300ms cubic-bezier(0.22,1,0.36,1), height 300ms cubic-bezier(0.22,1,0.36,1)';

  return (
    <>
      {/* Loom cursor — position driven by motion values, never by animate prop */}
      <motion.div
        className="fixed left-0 top-0 pointer-events-none z-[110] mix-blend-screen"
        style={{
          x: loomX,
          y: loomY,
          width: loomSize,
          height: loomSize,
          translateX: '-50%',
          translateY: '-50%',
          rotate: rotateSpring,
          transition: sizeTransition,
        }}
      >
        <motion.div
          className="absolute inset-0 rounded-full border border-[rgba(122,239,255,0.44)]"
          animate={{ rotate: 360 }}
          transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
        />
        <motion.div
          className="absolute inset-[12%] rounded-full border border-[rgba(255,255,255,0.28)]"
          animate={{ rotate: -360 }}
          transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
        />
        <div className="absolute inset-[24%] rounded-full border border-[rgba(56,189,248,0.24)] bg-[radial-gradient(circle_at_center,rgba(56,189,248,0.12)_0%,transparent_72%)]" />
        <div className="absolute inset-[39%] rounded-full bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.92)_0%,rgba(56,189,248,0.42)_48%,transparent_100%)] shadow-[0_0_24px_rgba(56,189,248,0.38)]" />
        <div className="absolute left-[15%] top-1/2 h-px w-[70%] -translate-y-1/2 bg-gradient-to-r from-transparent via-[#7dd3fc]/52 to-transparent" />
        <div className="absolute left-1/2 top-[15%] h-[70%] w-px -translate-x-1/2 bg-gradient-to-b from-transparent via-white/28 to-transparent" />
        <div className="absolute left-[18%] top-[26%] h-[52%] w-[64%] rounded-full border border-[rgba(56,189,248,0.16)] rotate-[24deg]" />
        <div className="absolute left-[18%] top-[26%] h-[52%] w-[64%] rounded-full border border-[rgba(240,140,255,0.16)] -rotate-[24deg]" />
      </motion.div>

      {/* Glow */}
      <motion.div
        className="fixed left-0 top-0 pointer-events-none z-[109] mix-blend-screen rounded-full bg-[radial-gradient(circle_at_center,rgba(56,189,248,0.34)_0%,rgba(56,189,248,0.08)_42%,transparent_76%)]"
        style={{
          x: glowX,
          y: glowY,
          width: glowSize,
          height: glowSize,
          opacity: glowOpacity,
          translateX: '-50%',
          translateY: '-50%',
          transition: `${sizeTransition}, opacity 300ms ease`,
        }}
      />

      {/* Dot */}
      <motion.div
        className="fixed left-0 top-0 h-2 w-2 rounded-full bg-white pointer-events-none z-[111] mix-blend-screen"
        style={{ x: dotX, y: dotY }}
      />
    </>
  );
}
